import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { getDossier } from "../../api/dossier/getDossier";
import { getDossierStatus } from "../../api/dossier/getDossierStatus";
import { createQuoteFinanceComparisonView } from "../../api/quote/createQuoteFinanceComparisonView";
import { getQuote } from "../../api/quote/getQuote";
import { getQuoteFinanceComparison } from "../../api/quote/getQuoteFinanceComparison";
import { patchQuoteFinanceComparisonView } from "../../api/quote/patchQuoteFinanceComparisonView";
import APContactHelpButton from "../../components/buttons/APContactHelpButton";
import BackButton from "../../components/buttons/BackButton";
import HomeButton from "../../components/buttons/HomeButton";
import InfoButton from "../../components/buttons/InfoButton";
import APComparison, {
  Comparison,
  ComparisonCriterion,
} from "../../components/comparison/APComparison";
import APComparisonSummary from "../../components/comparison/APComparisonSummary";
import PageContainer from "../../components/PageContainer";
import RestrictedAppScope from "../../components/RestrictedAppScope";
import QuoteComparisonName from "../../dossier/quoteComparison/components/QuoteComparisonName";
import {
  getSummarySortFn,
  parseComparisonParams,
} from "../../helpers/comparison";
import { toDollars } from "../../helpers/currencies";
import { useComparisonViewTracking } from "../../hooks/useComparisonViewTracking";
import { useFaqs } from "../../hooks/useFaqs";
import { useAuth } from "../../providers/AuthProvider";
import { buildNextActionRoute, NextDriverAction } from "../../types/Dossier";
import { ErrorCode } from "../../types/Error";
import { CRMAppLocationIdentifier } from "../../types/Faq";
import {
  CriteriaLabelMap,
  FinanceComparison,
  FinanceComparisonItem,
  FinanceMethodLabelMap,
} from "../../types/FinanceComparison";

const defaultSelectedCriteria: (keyof FinanceComparisonItem)[] = [
  "upfrontCost",
  "baseInterestRate",
  "savingsPercentage",
  "annualSavings",
];

const defaultBottomCriteria: (keyof FinanceComparisonItem)[] = [
  "upfrontCost",
  "baseInterestRate",
  "annualSavings",
  "savingsPercentage",
];
const defaultMethods: (keyof FinanceComparison)[] = [
  "novated",
  "cash",
  "homeloan",
  "carloan",
];

const methodToAppLocationIds = {
  lifetimeCost: {
    novated: "FinCompareView-LifetimeCostNovated",
    cash: "FinCompareView-LifetimeCostCash",
    homeloan: "FinCompareView-LifetimeCostHomeLoan",
    carloan: "FinCompareView-LifetimeCostCarFinance",
  } as Record<keyof FinanceComparison, CRMAppLocationIdentifier>,
  interestRate: {
    novated: "FinCompareView-InterestRateNovated",
    cash: "FinCompareView-InterestRateCash",
    homeloan: "FinCompareView-InterestRateHomeLoan",
    carloan: "FinCompareView-InterestRateCarLoan",
  } as Record<keyof FinanceComparison, CRMAppLocationIdentifier>,
};

const QuoteFinanceComparison = () => {
  const [searchParams] = useSearchParams();
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const { inAppScope, getScopedPageUrl } = useAuth();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const methodsParams = searchParams.get("methods")?.split(",");
  const selectedCriteria =
    searchParams.get("selectedCriteria")?.split(",") ?? defaultSelectedCriteria;
  const initialPrimaryCriteria =
    searchParams.get("primaryCriteria") ?? undefined;
  const financeComparison = getQuoteFinanceComparison(quoteId ?? "");

  let methods: (keyof FinanceComparison)[] = defaultMethods;

  if (methodsParams) {
    methods = methods.filter((method) => methodsParams.includes(method));
  }

  const quote = getQuote(quoteId ?? "", {
    mutationOptions: {
      errorNavPaths: { [ErrorCode.QuoteDeleted]: getScopedPageUrl("home") },
    },
  });

  const dossierId = quote.data?.identifiers.crmDossierId;
  const dossier = getDossier(dossierId || "", {
    disabled: !dossierId,
  });
  const dossierStatus = getDossierStatus(dossierId || "", {
    disabled: !dossierId,
  });

  // View tracking
  const { mutateAsync: mutateCreateQuoteFinanceComparisonViewAsync } =
    createQuoteFinanceComparisonView(quoteId || "");
  const { mutateAsync: mutatePatchQuoteFinanceComparisonViewAsync } =
    patchQuoteFinanceComparisonView(quoteId || "");

  const _createView = async () => {
    const _viewId = await mutateCreateQuoteFinanceComparisonViewAsync();

    return _viewId;
  };

  const _patchView = async (viewId: string, sortHistory: string[]) => {
    await mutatePatchQuoteFinanceComparisonViewAsync({
      financeComparisonViewId: viewId,
      clickedCriteria: sortHistory.join(","),
    });
  };

  const { onSort } = useComparisonViewTracking({
    createView: inAppScope("external")
      ? _createView
      : () => Promise.resolve(""),
    patchView: inAppScope("external") ? _patchView : () => Promise.resolve(),
  });

  if (quote.isError) {
    throw quote.error;
  }

  if (dossier.isError) {
    throw dossier.error;
  }

  if (dossierStatus.isError) {
    throw dossierStatus.error;
  }

  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId: "finance-compare-view",
    contextIds: {
      opportunityId: dossier.data?.opportunity?.id,
      quoteId: quoteId,
    },
  });

  const renderQuote = () => {
    const _quote = quote.data;
    return (
      <Box sx={{ width: "100%" }}>
        {_quote && (
          <Box sx={{ display: "flex", columnGap: 1, height: "40px" }}>
            <Box
              width="40px"
              height="40px"
              component="img"
              src={_quote.vehicle.primaryImageUrl ?? "/photo-placeholder.png"}
              alt={_quote.vehicle.make}
              sx={{ objectFit: "cover" }}
            />
            <Box>
              <Typography variant="bodySmall">
                {_quote.vehicle.make} {_quote.vehicle.model}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderMethod = (item: FinanceComparisonItem) => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          height: "60px",
        }}
      >
        <Typography variant="bodyLarge">
          {FinanceMethodLabelMap[item.method]}
        </Typography>

        <Box mt={1}>
          {item.method === "novated" && (
            <QuoteComparisonName
              key={`${quoteId}-${quote.data?.identifiers.name}`}
              id={quoteId || ""}
              dossierId={dossierId ?? ""}
              quoteComparisonIds={[]}
              name={quote.data?.identifiers.name || ""}
              isLocked={quote.data?.config.isLocked ?? true}
            />
          )}
        </Box>
      </Box>
    );
  };

  const buildComparison = (
    comparison: FinanceComparison
  ): Comparison<FinanceComparisonItem> => {
    const items: FinanceComparisonItem[] = methods.map((method) => ({
      method: method,
      upfrontCost: comparison[method].upfrontCost,
      baseInterestRate: comparison[method].baseInterestRate,
      savingsPercentage: comparison[method].savingsPercentage,
      annualSavings: comparison[method].annualSavings,
      lifetimeCostTotal: comparison[method].lifetimeCostTotal,
      lifetimeCostWeekly: comparison[method].lifetimeCostWeekly,
      termMonths: comparison[method].termMonths,
      summary: method,
    }));
    const {
      parsedInitialPrimaryCriteria,
      parsedSelectedCriteria,
      summaryCriteria,
    } = parseComparisonParams(
      items[0],
      initialPrimaryCriteria,
      "lifetimeCostWeekly",
      selectedCriteria,
      "summary"
    );

    const criteria: Partial<
      Record<
        keyof FinanceComparisonItem,
        ComparisonCriterion<FinanceComparisonItem>
      >
    > = {
      method: {
        label: "",
        labelRenderer: () => renderQuote(),
        valueRenderer: (item) => renderMethod(item),
      },
      upfrontCost: {
        label: CriteriaLabelMap.upfrontCost,
        sortable: {
          direction: "asc",
        },
        valueFormatter: (value) => toDollars(value as number, 0),
        getAppLocationId: () => "FinCompareView-UpfrontCost",
      },
      baseInterestRate: {
        label: CriteriaLabelMap.baseInterestRate,
        sortable: {
          direction: "asc",
        },
        valueFormatter: (value) => `${value}%`,
        getAppLocationId: (item) =>
          methodToAppLocationIds.interestRate[item.method],
      },
      savingsPercentage: {
        label: CriteriaLabelMap.savingsPercentage,
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) => `${value}%`,
        getAppLocationId: () => "FinCompareView-SavingsPct",
      },
      annualSavings: {
        label: CriteriaLabelMap.annualSavings,
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) => toDollars(value as number, 0),
        getAppLocationId: () => "FinCompareView-AnnTaxSavings",
      },
      lifetimeCostTotal: {
        label: CriteriaLabelMap.lifetimeCostTotal,
        sortable: {
          direction: "asc",
        },
        valueFormatter: (value) => toDollars(value as number, 0),
        getAppLocationId: (item) =>
          methodToAppLocationIds.lifetimeCost[item.method],
      },
      lifetimeCostWeekly: {
        label: CriteriaLabelMap.lifetimeCostWeekly,
        sortable: {
          direction: "asc",
        },
        valueFormatter: (value) => toDollars(value as number, 0),
        getAppLocationId: (item) =>
          methodToAppLocationIds.lifetimeCost[item.method],
      },
      termMonths: {
        label: CriteriaLabelMap.termMonths,
        sortable: {
          direction: "asc",
        },
        valueFormatter: (value) => `${value}mths`,
      },
    };

    return {
      items,
      criteria: {
        ...criteria,
        summary: {
          label: CriteriaLabelMap.summary,
          sortable: {
            direction: "desc",
            function: getSummarySortFn(items, criteria, summaryCriteria),
          },
          styles: {
            topAlign: true,
          },
          valueRenderer: (item) => (
            <APComparisonSummary
              selectedItem={item}
              items={items}
              criteria={criteria}
              summaryCriteria={summaryCriteria}
              criteriaLabelMap={CriteriaLabelMap}
            />
          ),
        },
      },
      headerCriteria: "method",
      initialPrimaryCriteria:
        parsedInitialPrimaryCriteria ?? "lifetimeCostWeekly",
      selectedCriteria: parsedSelectedCriteria,
      alwaysShownBottomCriteria: defaultBottomCriteria,
      summaryCriteria: "summary",
      expandableCriteria: [
        {
          id: "detail",
          label: "Detail",
          criteria: ["termMonths", "lifetimeCostTotal"],
          expandByDefault: false,
        },
      ],
      highlightWinner: true,
      faqProps,
      onSort,
    };
  };

  const isLoading =
    financeComparison.isLoading ||
    dossier.isLoading ||
    dossierStatus.isLoading ||
    isLoadingFaqs;
  const isSuccess =
    financeComparison.isSuccess &&
    dossier.isSuccess &&
    dossierStatus.isSuccess &&
    isSuccessFaqs;

  const comparison = isSuccess ? buildComparison(financeComparison.data) : null;
  const onNext = async (nextAction: NextDriverAction) => {
    if (dossierId) {
      const route = buildNextActionRoute(dossierId, nextAction);
      navigate(route);
    }
  };

  return (
    <PageContainer
      title="Compare Finance"
      loading={isLoading}
      containerRef={containerRef}
      titleButton={
        isSuccess ? (
          <Box mr={1}>
            {faqProps.renderAppLocation(
              "FinCompareView-Info",
              <InfoButton />,
              true,
              true
            )}
          </Box>
        ) : undefined
      }
    >
      {isSuccess && comparison && (
        <>
          <APComparison
            comparison={comparison}
            config={{
              widths: {
                label: 140,
                mobileLabel: 120,
                item: 146,
              },
            }}
            containerRef={containerRef}
          />
          <Box
            sx={{
              pt: 4,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Stack
              spacing={2}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Box display="flex" gap={1}>
                <BackButton /> <HomeButton destination="dossiersHome" />
              </Box>
              <RestrictedAppScope scope="external">
                <Box>
                  <APContactHelpButton
                    regardingOpportunityId={
                      dossier.data.opportunity?.id ?? undefined
                    }
                  />
                </Box>
              </RestrictedAppScope>
            </Stack>
            <Stack
              spacing={1}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Button
                size="large"
                color="secondary"
                variant="contained"
                disableElevation={true}
                startIcon={<PlayArrowOutlinedIcon />}
                onClick={() => onNext(dossierStatus.data.nextDriverAction)}
              >
                Next
              </Button>
            </Stack>
          </Box>
          <Box pt={2}>
            {faqProps.renderAppLocation(
              "FinCompareView-InterestRateFAQ",
              <Typography
                variant="bodySmall"
                color="primary"
                sx={{ textDecoration: "underline", cursor: "pointer" }}
              >
                ^Base Interest % FAQ
              </Typography>,
              true
            )}
          </Box>
        </>
      )}
    </PageContainer>
  );
};

export default QuoteFinanceComparison;
